import { Suspense, type ComponentType } from "react";
import { useQuery } from "@tanstack/react-query";
import { LoadingState } from "@signet/ui";
import { api, useMe } from "@signet/web-kit";
import { webModules } from "../../../../../modules/registry.web.ts";
import { ClaudeMaxCard } from "./claude-card.tsx";
import { MuleRouterCard } from "./mulerouter-card.tsx";
import { CORE_USAGE_NEEDS } from "./needs.ts";
import { RailwayCard } from "./railway-card.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   API/AI Usage: what every outside service Signet runs on costs, on one
   page (Jay, 2026-10-01). It joined the AI usage page and Research's API
   Usage tab.

   One grid of cards, all the same shape (UsageCard in the kit): the live
   providers read through the server, then a card per module that pays for a
   service (usagePanels in its web half), each shown only to people who hold
   its permission. The Claude API spend table and daily limits were taken
   off the page the same day at Jay's request; /api/admin/usage and the
   budgets themselves still work.
   ────────────────────────────────────────────────────────────────────────── */

// Card per live provider the server knows, by its id in /api/usage/providers.
// A new provider is a card file and one line here; the server's own list
// decides which are shown, so a card never asks for an endpoint that is not
// there yet.
const LIVE_CARDS: { id: string; component: ComponentType }[] = [
  { id: "claude", component: ClaudeMaxCard },
  { id: "railway", component: RailwayCard },
  { id: "mulerouter", component: MuleRouterCard },
];

type Providers = { providers: { id: string; label: string; configured: boolean }[] };

function LiveCards() {
  const providers = useQuery({ queryKey: ["usage-live", "providers"], queryFn: () => api<Providers>("/api/usage/providers"), staleTime: 5 * 60_000 });
  // Until the list answers, or if it cannot, every card is shown and each
  // reports its own state, rather than an empty row.
  const known = providers.data?.providers.map((p) => p.id);
  const cards = known ? known.flatMap((id) => LIVE_CARDS.filter((card) => card.id === id)) : LIVE_CARDS;
  return <>{cards.map((card) => <card.component key={card.id} />)}</>;
}

const panels = webModules.flatMap((mod) => mod.usagePanels ?? []).sort((a, b) => a.order - b.order);

export function UsagePage() {
  const { can } = useMe();
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>API/AI Usage</h1>
          <p>What Signet's outside services cost and how much of each is left: the Claude plan, hosting, credits and Instagram scans.</p>
        </div>
      </div>

      <div className="signet-usage-live">
        {can(CORE_USAGE_NEEDS) ? <LiveCards /> : null}
        {panels.filter((panel) => can(panel.needs)).map((panel) => (
          <Suspense key={panel.id} fallback={<LoadingState />}>
            <panel.component />
          </Suspense>
        ))}
      </div>
    </div>
  );
}
